// src/features/Analytics/hooks/useMLPredictionsTF.js
import { useState, useEffect, useCallback, useMemo } from 'react';
import { MLPredictorTF } from '../mlPredictorTF';
import { getData } from '../../../services/storageService';

export const useMLPredictionsTF = (options = {}) => {
  const {
    predictionDays = 7,
    autoRefresh = true,
    refreshInterval = 300000,
    useTensorFlow = true
  } = options;

  const [predictor] = useState(() => new MLPredictorTF());
  const [predictions, setPredictions] = useState([]);
  const [modelState, setModelState] = useState({
    isTrained: false,
    isLoading: false,
    error: null,
    lastUpdated: null,
    usingTensorFlow: false
  });

  const [trainingStats, setTrainingStats] = useState({
    totalRecords: 0,
    validRecords: 0,
    dataQuality: 0,
    modelAccuracy: 0,
    modelType: 'none'
  });

  const loadTrainingData = useCallback(async () => {
    setModelState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const records = await getData('lainsa_records');
      
      if (!Array.isArray(records) || records.length === 0) {
        throw new Error('No records found for ML training');
      }

      predictor.addTrainingData(records);

      const validRecords = predictor.trainingData.length;
      const dataQuality = validRecords > 0 ? 
        Math.round((validRecords / records.length) * 100) : 0;

      setTrainingStats(prev => ({
        ...prev,
        totalRecords: records.length,
        validRecords,
        dataQuality,
        modelAccuracy: 0
      }));

      return records;

    } catch (error) {
      console.error('ML Hook TF: Data loading error:', error);
      setModelState(prev => ({
        ...prev,
        error: `Data loading error: ${error.message}`,
        isLoading: false
      }));
      return null;
    }
  }, [predictor]);

  const trainModel = useCallback(async () => {
    setModelState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const trainedModel = await predictor.trainProductionModel();
      
      if (!trainedModel) {
        throw new Error('Failed to train model. Insufficient data.');
      }

      const modelStats = predictor.getModelStats();
      
      setTrainingStats(prev => ({
        ...prev,
        modelAccuracy: modelStats.accuracy,
        modelType: modelStats.modelType
      }));

      setModelState({
        isTrained: true,
        isLoading: false,
        error: null,
        lastUpdated: new Date(),
        usingTensorFlow: modelStats.usingTensorFlow || false
      });

      console.log(`ML Hook TF: Model trained! Type: ${modelStats.modelType}, Accuracy: ${modelStats.accuracy.toFixed(1)}%`);
      
      return trainedModel;

    } catch (error) {
      console.error('ML Hook TF: Training error:', error);
      setModelState(prev => ({
        ...prev,
        error: `Training error: ${error.message}`,
        isLoading: false
      }));
      return null;
    }
  }, [predictor]);

  const generatePredictions = useCallback(async () => {
    if (!modelState.isTrained && predictor.trainingData.length > 0) {
      console.log('ML Hook TF: Model not trained, attempting training...');
      const trained = await trainModel();
      if (!trained) return;
    }

    try {
      const newPredictions = await predictor.predictProduction(predictionDays);
      
      setPredictions(newPredictions);
      setModelState(prev => ({
        ...prev,
        lastUpdated: new Date()
      }));

      console.log(`ML Hook TF: Generated ${newPredictions.length} predictions using ${predictor.modelMetrics.modelType}`);

    } catch (error) {
      console.error('ML Hook TF: Prediction error:', error);
      setModelState(prev => ({
        ...prev,
        error: `Prediction error: ${error.message}`
      }));
    }
  }, [modelState.isTrained, predictor, predictionDays, trainModel]);

  const refreshPredictions = useCallback(async () => {
    console.log('ML Hook TF: Refreshing predictions...');
    
    setModelState(prev => ({ ...prev, isLoading: true }));

    try {
      const records = await loadTrainingData();
      if (!records) return;

      if (predictor.trainingData.length >= 5) {
        await trainModel();
      } else {
        console.log('ML Hook TF: Insufficient data for training');
        setModelState(prev => ({
          ...prev,
          isTrained: false,
          isLoading: false,
          error: 'Insufficient data for ML model training'
        }));
      }

      await generatePredictions();

    } catch (error) {
      console.error('ML Hook TF: Refresh error:', error);
      setModelState(prev => ({
        ...prev,
        error: `Refresh error: ${error.message}`,
        isLoading: false
      }));
    }
  }, [loadTrainingData, trainModel, generatePredictions, predictor]);

  useEffect(() => {
    let isMounted = true;

    const initialize = async () => {
      if (!isMounted) return;
      await refreshPredictions();
    };

    initialize();

    return () => {
      isMounted = false;
    };
  }, [refreshPredictions]);

  useEffect(() => {
    if (!autoRefresh || !modelState.isTrained) return;

    const interval = setInterval(() => {
      generatePredictions();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, modelState.isTrained, generatePredictions, refreshInterval]);

  const predictionStats = useMemo(() => {
    if (predictions.length === 0) {
      return {
        averageProduction: 0,
        totalProduction: 0,
        confidence: 0,
        trend: 'stable'
      };
    }

    const productions = predictions.map(p => p.predictedProduction);
    const confidences = predictions.map(p => p.confidence);
    
    const averageProduction = Math.round(
      productions.reduce((sum, prod) => sum + prod, 0) / productions.length
    );
    
    const totalProduction = productions.reduce((sum, prod) => sum + prod, 0);
    const averageConfidence = Math.round(
      confidences.reduce((sum, conf) => sum + conf, 0) / confidences.length
    );

    let trend = 'stable';
    if (predictions.length >= 2) {
      const first = predictions[0].predictedProduction;
      const last = predictions[predictions.length - 1].predictedProduction;
      const change = ((last - first) / first) * 100;
      
      if (change > 5) trend = 'up';
      else if (change < -5) trend = 'down';
    }

    return {
      averageProduction,
      totalProduction,
      confidence: averageConfidence,
      trend,
      days: predictions.length
    };
  }, [predictions]);

  const isReady = useMemo(() => {
    return modelState.isTrained && 
           predictions.length > 0 && 
           !modelState.isLoading && 
           !modelState.error;
  }, [modelState, predictions]);

  const modelInfo = useMemo(() => {
    const stats = predictor.getModelStats();
    
    return {
      isTrained: modelState.isTrained,
      accuracy: trainingStats.modelAccuracy,
      dataQuality: trainingStats.dataQuality,
      trainingSize: trainingStats.validRecords,
      lastTraining: stats.lastTraining,
      features: stats.featuresUsed,
      type: stats.modelType || 'fallback',
      usingTensorFlow: stats.usingTensorFlow || false,
      backend: stats.backend || 'none'
    };
  }, [modelState.isTrained, trainingStats, predictor]);

  return {
    // Прогнози
    predictions,
    predictionStats,
    
    // Стан моделі
    modelState: {
      ...modelState,
      isReady,
      hasData: trainingStats.validRecords > 0
    },
    
    // Інформація про модель
    modelInfo,
    trainingStats,
    
    // Методи
    refreshPredictions,
    generatePredictions,
    
    // Утиліти
    hasPredictions: predictions.length > 0,
    canTrain: trainingStats.validRecords >= 5,
    
    // Детальна інформація
    debug: {
      predictorInstance: predictor,
      rawPredictions: predictions,
      tensorFlowAvailable: predictor.isUsingTF
    }
  };
};

export default useMLPredictionsTF;